/**
 * A small set of line icons, drawn inline.
 *
 * Inline because the content security policy this application is served under
 * allows no external origins, and because an icon font would be a second
 * network request to render a navigation bar. Every one is `aria-hidden`: the
 * label beside it is what a screen reader should read.
 */

type IconProps = { className?: string };

function Icon({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <svg
      className={className ?? "nx-nav__icon"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const HomeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
  </Icon>
);

export const GraphIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="9" y="3" width="6" height="5" rx="1.2" />
    <rect x="3" y="16" width="6" height="5" rx="1.2" />
    <rect x="15" y="16" width="6" height="5" rx="1.2" />
    <path d="M12 8v4M6 16v-2h12v2" />
  </Icon>
);

export const SystemsIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="4" width="18" height="7" rx="1.6" />
    <rect x="3" y="13" width="18" height="7" rx="1.6" />
    <path d="M7 7.5h.01M7 16.5h.01" />
  </Icon>
);

export const EntitiesIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5 20c0-3.6 3.1-5.5 7-5.5s7 1.9 7 5.5" />
  </Icon>
);

export const ConsentIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3 5 6v5.5c0 4.3 3 8.2 7 9.5 4-1.3 7-5.2 7-9.5V6z" />
    <path d="m9.2 12 2 2 3.6-3.8" />
  </Icon>
);

export const EventsIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 12h3l2.5-6 3.5 13 3-9 1.8 4H21" />
  </Icon>
);

export const ReleaseIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9z" />
    <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
  </Icon>
);

export const OrgIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="3" width="10" height="18" rx="1.4" />
    <path d="M14 8h6v13h-6M7.5 7h3M7.5 11h3M7.5 15h3" />
  </Icon>
);

export const AuditIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3h9l4 4v14H6z" />
    <path d="M14 3v5h5M9 12h6M9 16h4" />
  </Icon>
);

export const WorkIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="7" width="18" height="13" rx="1.6" />
    <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3 12h18" />
  </Icon>
);

export const SearchIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6" />
    <path d="m20 20-4.5-4.5" />
  </Icon>
);

export const AskIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 15a3 3 0 0 1-3 3H9l-5 3V6a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3z" />
    <path d="M9.6 8.8a2.4 2.4 0 1 1 2.9 2.4v1.3" />
  </Icon>
);

export const BrainIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5.5a2.7 2.7 0 0 0-5 1.3A2.6 2.6 0 0 0 5.5 12 2.6 2.6 0 0 0 7 16.8 2.7 2.7 0 0 0 12 18z" />
    <path d="M12 5.5a2.7 2.7 0 0 1 5 1.3A2.6 2.6 0 0 1 18.5 12 2.6 2.6 0 0 1 17 16.8 2.7 2.7 0 0 1 12 18z" />
  </Icon>
);

export const MemoryIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="5" width="14" height="14" rx="2" />
    <path d="M9 9h6v6H9zM9 2.5V5M15 2.5V5M9 19v2.5M15 19v2.5M2.5 9H5M2.5 15H5M19 9h2.5M19 15h2.5" />
  </Icon>
);

export const AgentsIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="8" width="16" height="11" rx="2.2" />
    <path d="M12 4.5V8M8.5 13v1.5M15.5 13v1.5" />
    <circle cx="12" cy="3.4" r="1.2" />
  </Icon>
);

export const KeyIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="15" r="4" />
    <path d="M10.8 12.2 20 3M16.5 6.5l2.5 2.5M14 9l2 2" />
  </Icon>
);
